import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { HeatOverrideReason } from "src/store/api/generatedApi";

interface Props {
  reasonOptions: HeatOverrideReason[];
  reason: string | null;
  subreason: string | null;
  comment: string;
  commentRequired: boolean;
  onReasonChange: (reason: string) => void;
  onSubreasonChange: (subreason: string) => void;
  onCommentChange: (comment: string) => void;
}

export const subreasonOptionsFor = ({
  reasonOptions,
  reason,
}: {
  reasonOptions: HeatOverrideReason[];
  reason: string | null;
}): string[] =>
  reasonOptions.find((option) => option.reason === reason)?.subreasons ?? [];

export const OverrideReasonFields = ({
  reasonOptions,
  reason,
  subreason,
  comment,
  commentRequired,
  onReasonChange,
  onSubreasonChange,
  onCommentChange,
}: Props) => {
  const { t } = useTenantTranslation();
  const subreasonOptions = subreasonOptionsFor({ reasonOptions, reason });
  const isCommentMissing = commentRequired && comment.trim().length === 0;
  return (
    <>
      <FormControl required>
        <FormLabel sx={{ typography: "body2", mb: 1 }}>
          {t("overrideReason")}
        </FormLabel>
        <RadioGroup
          value={reason ?? ""}
          onChange={(event) => {
            onReasonChange(event.target.value);
          }}
        >
          {reasonOptions.map((option) => (
            <FormControlLabel
              key={option.reason}
              value={option.reason}
              control={<Radio />}
              label={option.reason}
            />
          ))}
        </RadioGroup>
      </FormControl>
      <TextField
        select
        required={subreasonOptions.length > 0}
        label={t("overrideSubreason")}
        value={subreason ?? ""}
        error={subreasonOptions.length > 0 && subreason === null}
        onChange={(event) => {
          onSubreasonChange(event.target.value);
        }}
        disabled={reason === null}
        fullWidth
      >
        {subreasonOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        required={commentRequired}
        label={t(commentRequired ? "comment" : "overrideComment")}
        error={isCommentMissing}
        helperText={
          isCommentMissing ? t("overrideCommentRequiredHint") : undefined
        }
        value={comment}
        onChange={(event) => {
          onCommentChange(event.target.value);
        }}
        multiline
        minRows={2}
        fullWidth
      />
    </>
  );
};
